import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { ServiceType } from '../types';
import { ADMIN_AREA, AppArea } from '../config/areaConfig';

interface AreaContextValue {
  /** Der Bereich, in dem gerade gearbeitet wird — Geschäftsfeld oder Verwaltung. */
  area: AppArea;
  /**
   * Das zuletzt gewählte Geschäftsfeld. Bleibt auch in der Verwaltung stehen:
   * Wer von dort zurückwechselt, will dahin zurück, wo er hergekommen ist — und
   * die Listen (Mitarbeiter, Kunden …) brauchen immer ein Feld, nie „Verwaltung".
   */
  serviceType: ServiceType;
  /** Bereich wechseln, während schon gearbeitet wird (Umschalter in der Seitenleiste). */
  setArea: (a: AppArea) => void;
  /**
   * Whether the user has actively confirmed an area for this session.
   * Reset on every login so the selection screen is shown each time.
   */
  isAreaSelected: boolean;
  /** Bereich von der Auswahlseite bestätigen. */
  selectArea: (a: AppArea) => void;
  /** Forget the confirmation (called on login/logout). */
  resetAreaSelection: () => void;
}

const AreaContext = createContext<AreaContextValue | undefined>(undefined);

const STORAGE_KEY = 'optivisit.serviceType';
const AREA_KEY = 'optivisit.area';
const SELECTION_KEY = 'optivisit.modeSelected';

export const AreaProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [serviceType, setServiceTypeState] = useState<ServiceType>(() => {
    const stored = localStorage.getItem(STORAGE_KEY) as ServiceType | null;
    return stored === ServiceType.LIVE_IN_CARE ? ServiceType.LIVE_IN_CARE : ServiceType.HOME_CARE;
  });

  const [area, setAreaState] = useState<AppArea>(() => {
    const stored = localStorage.getItem(AREA_KEY);
    // Nur die Verwaltung ist ein eigener Wert; alles andere ergibt sich aus dem
    // Geschäftsfeld, damit beide Angaben gar nicht erst auseinanderlaufen können.
    if (stored === ADMIN_AREA) return ADMIN_AREA;
    const field = localStorage.getItem(STORAGE_KEY) as ServiceType | null;
    return field === ServiceType.LIVE_IN_CARE ? ServiceType.LIVE_IN_CARE : ServiceType.HOME_CARE;
  });

  // Session-scoped: kept across reloads of the same tab session, but cleared
  // on login/logout so a fresh login always lands on the selection screen.
  const [isAreaSelected, setIsAreaSelected] = useState<boolean>(
    () => sessionStorage.getItem(SELECTION_KEY) === 'true'
  );

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, serviceType);
  }, [serviceType]);

  useEffect(() => {
    localStorage.setItem(AREA_KEY, area);
  }, [area]);

  const setArea = (a: AppArea) => {
    setAreaState(a);
    if (a !== ADMIN_AREA) setServiceTypeState(a);
  };

  const selectArea = (a: AppArea) => {
    setArea(a);
    setIsAreaSelected(true);
    sessionStorage.setItem(SELECTION_KEY, 'true');
  };

  const resetAreaSelection = () => {
    setIsAreaSelected(false);
    sessionStorage.removeItem(SELECTION_KEY);
  };

  return (
    <AreaContext.Provider
      value={{ area, serviceType, setArea, isAreaSelected, selectArea, resetAreaSelection }}
    >
      {children}
    </AreaContext.Provider>
  );
};

export const useArea = (): AreaContextValue => {
  const ctx = useContext(AreaContext);
  if (!ctx) throw new Error('useArea must be used within an AreaProvider');
  return ctx;
};
